import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import cloneDeep from 'lodash.clonedeep';
import scrollIntoView from 'scroll-into-view-if-needed';

import { SingleSelectDropdownConfig } from './interfaces/single-select-dropdown-config.interface';
import { SingleSelectDropdownCustomCssClasses } from './interfaces/single-select-dropdown-custom-css-classes.interface';
import { SingleSelectDropdownItem } from './interfaces/single-select-dropdown-item.interface';

import { DropdownHelperService } from 'Angular/services/dropdown-helper.service';
import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';

import { FeatureNeededComponent } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.component';
import deepEqual from 'Angular/utils/equal/deep-equal';

/**
 *
 * Single Select Dropdown Component
 *
 * Input Properties:
 *   buttonClass: The CSS class to be applied to the button that opens the dropdown
 *   config: Please see single-select-dropdown-config.interface.ts for an example config.
 *   customCssClasses: An object containing css classes that are applied to the dropdown container, opening button, and
 *     dropdown menu.
 *   customSublabel: subtitle of custom label to be displayed if we don't want item selections to be included in the dropdown label (unbolded).
 *   customLabel: title of custom label to be displayed if we don't want item selections to be included in the dropdown label.
 *   inputDisabled: Whether the dropdown should be disabled
 *   placeHolderText: The text that should be displayed as a placeholder before any item is selected, e.g. "Select"
 *   scrollToSelected: Whether the dropdown should automatically scroll to the selected item when it is opened
 *   selectedValue: The value that is currently selected in the dropdown
 *
 * Output properties:
 *  changeHandler: EventEmitter that emits the item's value whenever a new item is selected
 *  closeHandler: EventEmitter that emits whenever the dropdown is closed
 *
 */

@Component({
    standalone: false,
    selector: 'ycn-single-select-dropdown',
    templateUrl: './single-select-dropdown.template.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SingleSelectDropdownComponent implements AfterViewInit, OnChanges {
    @Input() config!: SingleSelectDropdownConfig;

    @Input() customCssClasses: SingleSelectDropdownCustomCssClasses = {};

    @Input() customLabel?: string;

    @Input() customSublabel?: string;

    @Input() inputDisabled: boolean = false;

    @Input() isInHeader: boolean = false;

    @Input() isCollapsible: boolean = false;

    @Input() placeholderText?: string;

    @Input() scrollToSelected: boolean = false;

    @Input() selectedValue!: any;

    @Input() blockInput: boolean = false;

    @Input() appendToBody: boolean = false;

    @Input() showDropdownToggle: boolean = true;

    @Output() changeHandler = new EventEmitter<any>();

    @Output() closeHandler = new EventEmitter<void>();

    @Output() openHandler = new EventEmitter<void>();

    @ViewChild('singleSelectDropdownMainMenu') el!: ElementRef;

    @ViewChild('scrollTargetEl') scrollTargetEl!: ElementRef;

    @ViewChild(FeatureNeededComponent) featureNeededModal!: FeatureNeededComponent;

    configCopy: SingleSelectDropdownConfig = {} as SingleSelectDropdownConfig;

    isOpen: boolean = false;

    isOverButton: boolean = false;

    isOverMenu: boolean = false;

    selectedItem: null | SingleSelectDropdownItem = null;

    constructor(
        private changeDetector: ChangeDetectorRef,
        private dropdownHelperService: DropdownHelperService,
        private featureService: FeatureService
    ) {}

    ngAfterViewInit(): void {
        const autoCloseHandler = (): void => {
            if (this.isOpen) {
                this.toggle();
            }
            this.changeDetector.markForCheck();
        };

        let dropdownElement = this.el.nativeElement;
        if (!this.appendToBody) dropdownElement = dropdownElement.parentNode;
        this.dropdownHelperService.setCloseHandler(dropdownElement, autoCloseHandler);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes.config && !!changes.config.currentValue) {
            this.configCopy = cloneDeep(changes.config.currentValue);
            this.selectItem(this.selectedValue);
        }

        if (changes.selectedValue) {
            const selectedValue = changes.selectedValue.currentValue;
            this.selectItem(selectedValue);
        }
    }

    toggle(): void {
        // Toggling is handled by hover events if it's a nested dropdown
        if (this.customCssClasses.dropdownContainer === 'dropdown-nested') {
            return;
        }

        if (!this.inputDisabled) {
            if (this.isOpen) {
                this.isOpen = false;
                this.closeHandler.emit();
            } else {
                this.isOpen = true;
                this.openHandler.emit();
                if (this.scrollToSelected && this.scrollTargetEl) {
                    // Scroll so that the selected dropdown item is visible
                    setTimeout(() => {
                        scrollIntoView(this.scrollTargetEl.nativeElement, {
                            block: 'center',
                            scrollMode: 'if-needed',
                        });
                    });
                }
            }
        } else {
            this.isOpen = false;
        }
    }

    enterButton(): void {
        if (this.customCssClasses.dropdownContainer !== 'dropdown-nested' && !this.appendToBody) return;
        if (this.inputDisabled) return;
        this.isOverButton = true;
        this.calculateIsOpen();
    }

    leaveButton(): void {
        if (this.customCssClasses.dropdownContainer !== 'dropdown-nested' && !this.appendToBody) return;
        if (this.inputDisabled) return;
        this.isOverButton = false;
        this.calculateIsOpen();
    }

    enterMenu(): void {
        if (this.customCssClasses.dropdownContainer !== 'dropdown-nested' && !this.appendToBody) return;
        if (this.inputDisabled) return;
        this.isOverMenu = true;
        this.calculateIsOpen();
    }

    leaveMenu(): void {
        if (this.customCssClasses.dropdownContainer !== 'dropdown-nested' && !this.appendToBody) return;
        if (this.inputDisabled) return;
        this.isOverMenu = false;
        this.calculateIsOpen();
    }

    calculateIsOpen(): void {
        if (this.inputDisabled) {
            this.isOpen = false;
            return;
        }
        window.dispatchEvent(new Event('resize'));
        this.isOpen = this.isOverButton || this.isOverMenu;

        if (this.isOpen) {
            this.openHandler.emit();
            if (this.scrollToSelected && this.scrollTargetEl) {
                // Scroll so that the selected dropdown item is visible
                setTimeout(() => {
                    scrollIntoView(this.scrollTargetEl.nativeElement, {
                        block: 'center',
                        scrollMode: 'if-needed',
                    });
                });
            }
        } else {
            this.closeHandler.emit();
        }
    }

    handleChange(item: SingleSelectDropdownItem): void {
        // There appears to be a small bug with ngModel + checkbox. Workaround by manually detecting changes for now
        // https://github.com/angular/angular/issues/9275
        this.changeDetector.detectChanges();

        if (this.selectItem(item.value)) {
            this.changeHandler.emit(item.value);
        }
        this.toggle();
    }

    private purgeSelectedValue(selectedValue: string): void {
        for (let i = 0; i < this.configCopy.list.length; i += 1) {
            const listItem = this.configCopy.list[i];
            if (listItem.value === selectedValue) listItem.selected = false;
            if (listItem.sublist) {
                for (let j = 0; j < listItem.sublist.length; j += 1) {
                    const sublistItem = listItem.sublist[j];
                    if (sublistItem.value === selectedValue) sublistItem.selected = false;
                }
            }
        }
    }

    selectItem(selectedValue: string): boolean {
        // Return true or false to validate that item was selected

        // Some components may set the selected item to an empty string when resetting data back to the default state
        if (selectedValue === '') {
            this.selectedItem = null;
        }

        const selectedConfig = this.configCopy.list.find((item) => item.value === selectedValue);
        if (
            selectedConfig?.requiredFeature != null &&
            !this.featureService.userHasFeatures([selectedConfig.requiredFeature])
        ) {
            this.featureNeededModal.openModal(selectedConfig.requiredFeature);
            this.purgeSelectedValue(selectedValue);
            this.changeDetector.markForCheck();
            return false;
        }

        // Go through and deselect/select the correct items.
        for (let i = 0; i < this.configCopy.list.length; i += 1) {
            const listItem = this.configCopy.list[i];
            if (deepEqual(listItem.value, selectedValue)) {
                this.selectedItem = listItem;
                listItem.selected = true;
            } else {
                listItem.selected = false;
            }

            if (listItem.sublist) {
                for (let j = 0; j < listItem.sublist.length; j += 1) {
                    const sublistItem = listItem.sublist[j];
                    if (sublistItem.value === selectedValue) {
                        this.selectedItem = sublistItem;
                        sublistItem.selected = true;
                        listItem.open = true;
                    } else {
                        sublistItem.selected = false;
                    }
                }
            }
        }
        this.changeDetector.markForCheck();
        return true;
    }

    toggleSublist(item: SingleSelectDropdownItem): void {
        if (!item.sublist) return;
        // If an item in the sublist is selected, keep it open.
        // otherwise, toggle the open/closed state of the sublist.
        let isOpen = !item.open;

        for (let i = 0; i < item.sublist.length; i += 1) {
            const sublistItem = item.sublist[i];
            if (sublistItem.selected) {
                isOpen = true;
                break;
            }
        }
        item.open = isOpen; /* eslint-disable-line no-param-reassign */
    }
}
